import log from "logbeacon";
import type * as d3 from "d3";
import {
  getSizes,
  getSVG,
  handleChartResize,
  createOrSelectGroup,
  getTransition,
} from "./utils";
import type { PieChartOptions, PieChartDataItem } from "./type";

const PIE_CONFIG = {
  INNER_RADIUS_RATIO: 0.5,
  OUTER_RADIUS_RATIO: 0.85,
  HOVER_EXPAND_PX: 6,
  TRANSITION_DURATION: 150,
  TOOLTIP_OFFSET: 10,
  PAD_ANGLE: 0.02,
  CORNER_RADIUS: 4,
  MIN_PERCENTAGE: 0.03, // 最小占比 2%，防止小扇形 hover 闪动
} as const;

type PieArcDatum = d3.PieArcDatum<PieChartDataItem>;

const calculateRadii = (containerEl: HTMLElement) => {
  const radius =
    Math.min(containerEl.clientWidth, containerEl.clientHeight) / 2;
  return {
    innerRadius: radius * PIE_CONFIG.INNER_RADIUS_RATIO,
    outerRadius: radius * PIE_CONFIG.OUTER_RADIUS_RATIO,
  };
};

const createArc = (innerRadius: number, outerRadius: number) =>
  window.d3
    .arc<PieArcDatum>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .cornerRadius(PIE_CONFIG.CORNER_RADIUS);

const getSegmentColor = (d: PieArcDatum, activeKey: string | null) =>
  activeKey && activeKey !== d.data.key ? d.data.hiddenColor : d.data.color;

const calculateTooltipPosition = (
  event: MouseEvent,
  tooltipRect: DOMRect,
  containerRect: DOMRect,
) => {
  const mouseX = event.clientX - containerRect.left;
  const mouseY = event.clientY - containerRect.top;
  const offset = PIE_CONFIG.TOOLTIP_OFFSET;

  let tooltipX = mouseX + offset;
  let tooltipY = mouseY + offset;

  if (tooltipX + tooltipRect.width > containerRect.width) {
    tooltipX = mouseX - tooltipRect.width - offset;
  }
  if (tooltipY + tooltipRect.height > containerRect.height) {
    tooltipY = mouseY - tooltipRect.height - offset;
  }

  tooltipX = Math.max(
    0,
    Math.min(tooltipX, containerRect.width - tooltipRect.width),
  );
  tooltipY = Math.max(
    0,
    Math.min(tooltipY, containerRect.height - tooltipRect.height),
  );

  return { tooltipX, tooltipY };
};

/**
 * 预处理饼图数据，确保每个扇形至少占据最小比例
 * @param rawData 原始数据
 * @param minPercentage 最小占比（如 0.03 表示 3%）
 * @returns 处理后的数据，包含原始值
 */
const preprocessPieData = (
  rawData: PieChartDataItem[],
  minPercentage: number,
): Array<PieChartDataItem & { originalValue: number }> => {
  // 计算总值
  const total = rawData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return rawData.map((item) => ({ ...item, originalValue: item.value }));
  }

  // 预处理数据：对小于最小占比的项应用最小值
  return rawData.map((item) => {
    const actualPercentage = item.value / total;

    // 如果占比小于最小值，使用最小占比对应的值
    if (actualPercentage < minPercentage) {
      return {
        ...item,
        originalValue: item.value, // 保存原始值用于 tooltip
        value: total * minPercentage, // 用于显示的值（最小占比）
      };
    }

    return {
      ...item,
      originalValue: item.value,
    };
  });
};

export const drawPieChart = (
  options: PieChartOptions,
): { cleanup: () => void; setHoverKey: (key: string | null) => void } => {
  const { el, data, centerContent, tooltipFormatter, onHoverChange } = options;

  const containerEl =
    typeof el === "string" ? document.querySelector<HTMLElement>(el) : el;

  if (!containerEl) {
    log.error("[文案]PieChart: Container element not found");
    return { cleanup: () => {}, setHoverKey: () => {} };
  }

  if (!data?.length) {
    log.error("[文案]PieChart: Data is required");
    return { cleanup: () => {}, setHoverKey: () => {} };
  }

  const containerId = `pie-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  let currentHoverKey: string | null = null;

  const d3 = window.d3;
  d3.select(containerEl).style("position", "relative");

  const tooltip = d3
    .select(containerEl)
    .selectAll(`.pie-chart-tooltip-${containerId}`)
    .data([null])
    .join("div")
    .attr(
      "class",
      `pie-chart-tooltip-${containerId} absolute pointer-events-none opacity-0 z-[99999] top-0 left-0`,
    );

  const centerDiv = d3
    .select(containerEl)
    .selectAll(`.pie-chart-center-${containerId}`)
    .data([null])
    .join("div")
    .attr(
      "class",
      `pie-chart-center-${containerId} absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none`,
    );

  const showTooltip = (
    d: d3.PieArcDatum<PieChartDataItem & { originalValue: number }>,
    event: MouseEvent,
  ) => {
    // 使用原始值显示 tooltip，保持数据准确性
    const dataForTooltip = {
      ...d.data,
      value: d.data.originalValue || d.data.value,
    };

    const content = tooltipFormatter
      ? tooltipFormatter(dataForTooltip)
      : `${dataForTooltip.label}: ${dataForTooltip.value}`;

    tooltip.html(content).attr("class", (_, i, nodes) => {
      const currentClass = d3.select(nodes[i]).attr("class") || "";
      return currentClass.replace("opacity-0", "opacity-0");
    });

    const tooltipNode = tooltip.node() as HTMLElement;
    if (!tooltipNode) {
      return;
    }

    const { tooltipX, tooltipY } = calculateTooltipPosition(
      event,
      tooltipNode.getBoundingClientRect(),
      containerEl.getBoundingClientRect(),
    );

    tooltip
      .attr("class", (_, i, nodes) => {
        const currentClass = d3.select(nodes[i]).attr("class") || "";
        return currentClass.replace("opacity-0", "opacity-100");
      })
      .style("transform", `translate(${tooltipX}px, ${tooltipY}px)`);
  };

  const hideTooltip = () =>
    tooltip.attr("class", (_, i, nodes) => {
      const currentClass = d3.select(nodes[i]).attr("class") || "";
      return currentClass.replace("opacity-100", "opacity-0");
    });

  const updateTooltipPosition = (event: MouseEvent) => {
    const tooltipNode = tooltip.node() as HTMLElement;
    if (!tooltipNode) {
      return;
    }

    const { tooltipX, tooltipY } = calculateTooltipPosition(
      event,
      tooltipNode.getBoundingClientRect(),
      containerEl.getBoundingClientRect(),
    );

    tooltip.style("transform", `translate(${tooltipX}px, ${tooltipY}px)`);
  };

  const draw = () => {
    const { containerWidth, containerHeight } = getSizes(containerEl);
    const svg = getSVG(containerEl, "pie-chart");
    if (!svg) {
      return;
    }
    log.debug(
      "[drawPieChart] svg, containerWidth, containerHeight:",
      svg,
      containerWidth,
      containerHeight,
    );
    if (svg.select(".chart-group").empty()) {
      svg.html("");
    }

    const { innerRadius, outerRadius } = calculateRadii(containerEl);
    const chartGroup = createOrSelectGroup(svg, "chart-group").attr(
      "transform",
      `translate(${containerWidth / 2}, ${containerHeight / 2})`,
    );

    // 预处理数据，应用最小占比限制
    const processedData = preprocessPieData(data, PIE_CONFIG.MIN_PERCENTAGE);

    const pie = d3
      .pie<PieChartDataItem & { originalValue: number }>()
      .value((d) => d.value)
      .padAngle(PIE_CONFIG.PAD_ANGLE)
      .sort(null);

    const arc = createArc(innerRadius, outerRadius);
    const arcHover = createArc(
      innerRadius,
      outerRadius + PIE_CONFIG.HOVER_EXPAND_PX,
    );

    const pieData = pie(processedData);
    const arcs = chartGroup
      .selectAll<SVGPathElement, PieArcDatum>(".arc")
      .data(pieData, (d) => d.data.key);

    const t = getTransition();
    arcs.exit().transition(t).attr("opacity", 0).remove();

    const enterArcs = arcs
      .enter()
      .append("path")
      .attr("class", "arc cursor-pointer")
      .attr("d", arc)
      .attr("fill", (d) => getSegmentColor(d, currentHoverKey))
      .attr("opacity", 0);

    const allArcs = arcs.merge(enterArcs);
    allArcs.transition(t).attr("opacity", 1).attr("d", arc);

    const handleHover = function (event: any, d: any) {
      currentHoverKey = d.data.key;
      showTooltip(d, event as MouseEvent);

      allArcs.each(function (arcData) {
        const path = d3.select(this);
        const isHovered = arcData.data.key === d.data.key;

        path
          .transition()
          .duration(PIE_CONFIG.TRANSITION_DURATION)
          .attr(
            "d",
            (d) =>
              (isHovered
                ? arcHover(d as PieArcDatum)
                : arc(d as PieArcDatum)) || "",
          )
          .attr("fill", getSegmentColor(arcData, currentHoverKey));
      });

      onHoverChange?.(currentHoverKey);
    };

    const handleMouseOut = () => {
      currentHoverKey = null;
      hideTooltip();

      allArcs
        .transition()
        .duration(PIE_CONFIG.TRANSITION_DURATION)
        .attr("d", (d) => arc(d) || "")
        .attr("fill", (d) => getSegmentColor(d, null));

      onHoverChange?.(null);
    };

    allArcs
      .on("mouseover", handleHover)
      .on("mousemove", function (event) {
        updateTooltipPosition(event as MouseEvent);
      })
      .on("mouseout", handleMouseOut);
    if (centerContent) {
      const centerHtml = `
          <div class="headline_regular_m text-content_primary">${centerContent.value}</div>
          ${centerContent.label ? `<div class="body_s text-content_tertiary">${centerContent.label}</div>` : ""}
        `;
      centerDiv.html(centerHtml);
    }
  };

  draw();

  const { cleanup: resizeCleanup } = handleChartResize({
    el: containerEl,
    drawChart: draw,
  });

  const setHoverKey = (key: string | null) => {
    const svg = d3.select(containerEl).select<SVGSVGElement>("svg.pie-chart");
    currentHoverKey = key;

    const { innerRadius, outerRadius } = calculateRadii(containerEl);
    const arc = createArc(innerRadius, outerRadius);
    const arcHover = createArc(
      innerRadius,
      outerRadius + PIE_CONFIG.HOVER_EXPAND_PX,
    );

    if (key === null) {
      hideTooltip();
      svg
        .selectAll<SVGPathElement, PieArcDatum>(".arc")
        .transition()
        .duration(PIE_CONFIG.TRANSITION_DURATION)
        .attr("d", (d) => arc(d) || "")
        .attr("fill", (d) => getSegmentColor(d, null));
    } else {
      svg
        .selectAll<SVGPathElement, PieArcDatum>(".arc")
        .transition()
        .duration(PIE_CONFIG.TRANSITION_DURATION)
        .attr("d", (d) => (d.data.key === key ? arcHover(d) : arc(d)) || "")
        .attr("fill", (d) => getSegmentColor(d, key));
    }

    onHoverChange?.(key);
  };

  return {
    cleanup: () => {
      resizeCleanup();
      d3.select(`.pie-chart-tooltip-${containerId}`).remove();
      d3.select(`.pie-chart-center-${containerId}`).remove();
      d3.select(containerEl)
        .on("mouseover", null)
        .on("mousemove", null)
        .on("mouseout", null);
    },
    setHoverKey,
  };
};
